/**
 * ЭТАП 4 — ОБЁРТКА КЭША НАД `queryOptions`.
 *
 * Точка врезки выбрана именно здесь (`docs/SSR_REDIS_CACHE_PLAN.md`,
 * «Точка врезки»): TTL живёт рядом с запросом, там же, где `staleTime`,
 * включается по одному запросу, и клиента не касается вовсе.
 *
 * ПОЧЕМУ ЭТО ВООБЩЕ НУЖНО. Под SSR QueryClient создаётся на каждый запрос
 * (per-request изоляция в `src/server.ts`), поэтому `staleTime` на сервере
 * не работает в принципе: клиент каждый раз пустой. Верхнее меню, мета и
 * дерево категорий тянутся из бэка на КАЖДЫЙ хит, хотя меняются раз в
 * неделю. Кэш закрывает именно это.
 *
 * ⚠️ ПОЧЕМУ `redisClient` ИМПОРТИРУЕТСЯ ДИНАМИЧЕСКИ И ВНУТРИ `if`.
 * Этот модуль тянут фабрики `queryOptions`, то есть он В КЛИЕНТСКОМ ГРАФЕ.
 * Статический импорт `./redisClient` затащил бы в браузерный бандл ioredis
 * вместе с `node:net`. `import.meta.env.SSR` подставляется на сборке
 * константой, поэтому в клиентской сборке ветка целиком мертва и вырезается
 * вместе с импортом. Правило простое: ничего серверного на верхнем уровне
 * этого файла.
 *
 * НА КЛИЕНТЕ ОБЁРТКА — NO-OP: возвращаем опции как есть. В браузере уже
 * есть свой кэш (тот самый `staleTime`), а лезть в Redis оттуда нечем и
 * незачем.
 *
 * КЭШИРУЕМ ТОЛЬКО БЕЗЛИЧНОЕ. Ключ не должен содержать `cartToken`,
 * `headerToken`, `x-store-id` или id пользователя — иначе ответ одного
 * пользователя уедет другому. Проверка машинная, а не «мы помним»:
 * подозрительный ключ просто не кэшируется, и в лог падает предупреждение.
 *
 * ⚠️ НО КЛЮЧ — НЕ ЕДИНСТВЕННЫЙ ИСТОЧНИК ПЕРСОНАЛЬНОСТИ, и эта проверка НЕ
 * заменяет чтение кода. Живой пример: у `pickupPointByIdQueryOptions` ключ
 * безличный (`['pickupPointById', storeId]`), а `queryFn` передаёт в бэк
 * `cartToken`, и ответ считается от корзины. Автоматика такое пропустит.
 * Поэтому перед тем, как обернуть запрос, смотрим и на аргументы `queryFn`,
 * и при сомнении — на резолвер.
 */
import type { TtlProfile } from './keys';

export { TTL_PROFILES, resolveTtlSeconds, buildCacheKey } from './keys';
export type { TtlProfile } from './keys';

/** Признаки персональности в ключе — при их наличии кэш выключается. */
const PERSONAL_MARKERS = [
  'carttoken',
  'headertoken',
  'peopletoken',
  'x-store-id',
  'xstoreid',
  'people_id',
  'peopleid',
  // Адрес доставки: от него зависят наличие и сроки
  'addressid',
  // «Мои вопросы» на /faq: с true бэк отдаёт выдачу конкретного человека
  'mepeopleidfilter',
  'mepeopleid'
];

/**
 * Персональным считаем ТОЛЬКО непустое значение маркера. `xStoreId: null` и
 * `mePeopleIdFilter: false` — это пристин-варианты, которые SSR и получает
 * (на сервере нет localStorage, заголовок не уходит), их кэшировать можно и
 * нужно. Проверка именно на «истинность», а не на «не null»: у флагов
 * пристин-значение — `false`, и оно обязано считаться безличным.
 */
const hasPersonalData = (queryKey: readonly unknown[]): boolean => {
  const walk = (value: unknown): boolean => {
    if (value === null || value === undefined) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.some(walk);
    }
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).some(
        ([key, item]) => {
          if (PERSONAL_MARKERS.includes(key.toLowerCase())) {
            return Boolean(item);
          }
          return walk(item);
        }
      );
    }
    return false;
  };
  return queryKey.some(walk);
};

type QueryOptionsLike = {
  queryKey: readonly unknown[];
  queryFn?: unknown;
};

/**
 * Оборачивает `queryFn` серверным кэшем. Возвращает те же опции: ключ,
 * `staleTime` и всё остальное не трогаем — для react-query ничего не
 * меняется.
 *
 * `personalValues` — для ПОЗИЦИОННЫХ ключей. Проверка выше ищет маркеры по
 * ИМЕНИ поля, а во многих товарных фабриках `xStoreId` лежит в ключе просто
 * значением (`['productInfoTemp', id, isMenu, xStoreId]`) — имени нет, и
 * автоматика такой ключ не разберёт. Тогда вызывающий передаёт эти значения
 * сюда явно: непустое хотя бы одно — кэш выключен. На SSR они пристин-пустые
 * (на сервере нет localStorage, заголовки `x-store-id`/`X-Cart-Token` уходят
 * пустыми), так что кэшируется ровно то, что видит аноним.
 */
export const withSsrCache = <T extends QueryOptionsLike>(
  options: T,
  profile: TtlProfile = 'standard',
  personalValues: readonly unknown[] = []
): T => {
  if (import.meta.env.SSR) {
    const originalFn = options.queryFn;
    if (typeof originalFn !== 'function') {
      return options;
    }
    if (personalValues.some(Boolean)) {
      return options;
    }
    if (hasPersonalData(options.queryKey)) {
      console.log(
        '[ssr-cache] ключ выглядит персональным, кэш для него выключен:',
        options.queryKey
      );
      return options;
    }

    const { queryKey } = options;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callOriginal = originalFn as (...a: any[]) => Promise<unknown>;

    return {
      ...options,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryFn: async (...args: any[]) => {
        // У бесконечных запросов queryFn зовётся ОТДЕЛЬНО НА КАЖДУЮ
        // СТРАНИЦУ, а queryKey у всех страниц один. Без страницы в ключе
        // вторая страница перетёрла бы первую, и список отдавался бы
        // вперемешку. У обычных запросов pageParam нет — ключ остаётся
        // прежним, кэш уровней 1–2 не инвалидируется.
        const { cacheGet, cacheSet } = await import('./redisClient');
        const pageParam = (args[0] as { pageParam?: unknown } | undefined)
          ?.pageParam;
        const cacheKey =
          pageParam === undefined ? queryKey : [...queryKey, { pageParam }];

        const cached = await cacheGet<unknown>(cacheKey);
        if (cached.hit) {
          return cached.value;
        }
        const fresh = await callOriginal(...args);
        // Ответ страницы запись не ждёт — см. «fire-and-forget» в плане
        void cacheSet(cacheKey, fresh, profile);
        return fresh;
      }
    } as unknown as T;
  }

  return options;
};
